// src/App.js
import { useState, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import SplashScreen from "./components/SplashScreen";
import DashboardLayout from "./layouts/DashboardLayout";
import ItemsList from "./pages/ItemsList";
import Transactions from "./pages/Transactions";
import DamagedItems from "./pages/DamagedItems";
import Settings from "./pages/Settings";
import Users from "./pages/Users";
import Roles from "./pages/Roles";
import Audit from "./pages/Audit";
import DebugLog from "./pages/DebugLog";
import Login from "./pages/Login";
import Categories from "./pages/Categories";
import Locations from "./pages/Locations";
import MyTransactions from "./pages/MyTransactions";
import Profile from "./pages/Profile";
import BorrowItem from "./pages/BorrowItem";
import PrintBorrowForm from "./pages/PrintBorrowForm";
import Cart from "./pages/Cart";
import AdminBorrowBatches from "./pages/AdminBorrowBatches";
import AdminApproveBatch from "./pages/AdminApproveBatch";
import { BorrowCartProvider } from "./context/BorrowCartContext";
import { isTokenExpired } from "./utils/jwt";
import { installAuthGuard } from "./utils/authGuard";
import ActiveBorrows from "./pages/ActiveBorrows";
import ReturnBatch from "./pages/ReturnBatch";
import EditBorrowBatch from "./pages/EditBorrowBatch";
import TempItemsList from "./pages/TempItemsList";
import PanelTemplates from "./pages/PanelTemplates";
import PanelAssemblies from "./pages/PanelAssemblies";
import AdminPanelAssemblies from "./pages/AdminPanelAssemblies";
import LoanTransactions from "./pages/LoanTransactions";
import MyLoanRequests from "./pages/MyLoanRequests";
import OrderPanel from "./pages/OrderPanel";
import Dashboard from "./pages/Dashboard";
import SharedLoanRekap from "./pages/SharedLoanRekap";
import SharedOutgoingRekap from "./pages/SharedOutgoingRekap";
import OutgoingItems from "./pages/OutgoingItems";
import AdmCategoryPage from "./pages/administrasi/AdmCategoryPage";
import ArsipDokumen from "./pages/administrasi/ArsipDokumen";
import Anggaran from "./pages/administrasi/Anggaran";
import RealisasiNota from "./pages/administrasi/RealisasiNota";
import KasMasuk from "./pages/administrasi/KasMasuk";
import KasKeluarRekap from "./pages/administrasi/KasKeluarRekap";
import EventFolderConfig from "./pages/administrasi/EventFolderConfig";
import TemplateManager from "./pages/administrasi/TemplateManager";

// 🔹 GLOBAL TOKEN CHECK (runs before React mounts)
// /rekap is a public, no-login share link — never force-redirect it to /login.
const PUBLIC_PATH_PREFIXES = ["/login", "/rekap"];
const token = localStorage.getItem("accessToken");
if (!token || isTokenExpired(token)) {
  localStorage.removeItem("accessToken");
  if (!PUBLIC_PATH_PREFIXES.some((p) => window.location.pathname.startsWith(p))) {
    window.location.href = "/login";
  }
}

// Catches a suspension that happens mid-session (after this initial check
// already passed) on whichever page's API call hits it next.
installAuthGuard();

// Boot-sequence splash screen — off by default cost (a few extra API calls)
// isn't worth it. Toggle with REACT_APP_SHOW_SPLASH in .env.
const SHOW_SPLASH = process.env.REACT_APP_SHOW_SPLASH !== "false";

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    if (!SHOW_SPLASH) return false;
    const tok = localStorage.getItem("accessToken");
    return !!tok && !isTokenExpired(tok);
  });
  const handleSplashDone = useCallback(() => setShowSplash(false), []);

  return (
    <>
      {showSplash && <SplashScreen onDone={handleSplashDone} />}
      <BorrowCartProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/rekap/pinjaman" element={<SharedLoanRekap />} />
            {/* Public, no-login rekap of barang that hasn't come back —
                same shape as /rekap/pinjaman, must stay outside the
                authenticated layout below. */}
            <Route path="/rekap/barang-keluar" element={<SharedOutgoingRekap />} />

            <Route
              path="/*"
              element={
                <DashboardLayout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/items" element={<ItemsList />} />
                    <Route path="/categories" element={<Categories />} />
                    <Route path="/locations" element={<Locations />} />
                    <Route path="/profile" element={<Profile />} />

                    <Route path="/cart" element={<Cart />} />
                    <Route path="/admin/borrow-batches/pending" element={<AdminBorrowBatches />} />
                    <Route path="/borrow-batches/:batchId/edit" element={<EditBorrowBatch />} />
                    <Route path="/admin/borrow-batches/:batchId/approval" element={<AdminApproveBatch />} />

                    <Route path="/borrow/:itemId" element={<BorrowItem />} />

                    <Route path="/admin/borrow-batches" element={<AdminBorrowBatches />} />
                    <Route path="/active-borrows" element={<ActiveBorrows />} />
                    <Route path="/admin/borrow-batches/:batchId/return" element={<ReturnBatch />} />

                    <Route path="/borrow-requests/print" element={<PrintBorrowForm />} />
                    <Route path="/transactions" element={<Transactions />} />
                    <Route path="/barang-di-luar" element={<OutgoingItems />} />
                    <Route path="/damaged" element={<DamagedItems />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/audit" element={<Audit />} />
                    <Route path="/debug-log" element={<DebugLog />} />
                    <Route path="/my-transactions" element={<MyTransactions />} />
                    <Route path="/settings" element={<Settings />} />

                    <Route path="/temporary-items" element={<TempItemsList />} />

                    <Route path="/panel-templates" element={<PanelTemplates />} />
                    <Route path="/panel-assemblies" element={<PanelAssemblies />} />
                    <Route path="/admin/panel-assemblies" element={<AdminPanelAssemblies />} />
                    <Route path="/loan-transactions" element={<LoanTransactions />} />
                    <Route path="/my-loan-requests" element={<MyLoanRequests />} />
                    <Route path="/order-panel" element={<OrderPanel />} />

                    {/* Administrasi — Sekretaris */}
                    <Route
                      path="/administrasi/sekretaris/form-pengajuan"
                      element={<AdmCategoryPage module="sekretaris" category="form_pengajuan" title="Form Pengajuan" />}
                    />
                    <Route
                      path="/administrasi/sekretaris/surat-masuk"
                      element={<AdmCategoryPage module="sekretaris" category="surat_masuk" title="Surat Masuk" />}
                    />
                    <Route
                      path="/administrasi/sekretaris/surat-keluar"
                      element={<AdmCategoryPage module="sekretaris" category="surat_keluar" title="Surat Keluar" />}
                    />
                    <Route path="/administrasi/sekretaris/arsip" element={<ArsipDokumen />} />

                    {/* Administrasi — Bendahara */}
                    <Route
                      path="/administrasi/bendahara/pengajuan-dana"
                      element={<AdmCategoryPage module="bendahara" category="pengajuan_dana" title="Pengajuan Dana" />}
                    />
                    <Route path="/administrasi/bendahara/kas-masuk" element={<KasMasuk />} />
                    <Route path="/administrasi/bendahara/kas-keluar" element={<KasKeluarRekap />} />
                    <Route path="/administrasi/bendahara/anggaran" element={<Anggaran />} />
                    <Route path="/administrasi/bendahara/realisasi-nota" element={<RealisasiNota />} />
                    <Route
                      path="/administrasi/bendahara/lpj"
                      element={<AdmCategoryPage module="bendahara" category="lpj" title="LPJ" />}
                    />

                    {/* Master Data — Super Admin */}
                    <Route path="/roles" element={<Roles />} />
                    <Route path="/administrasi/folder-config" element={<EventFolderConfig />} />
                    <Route path="/administrasi/template-manager" element={<TemplateManager />} />
                  </Routes>
                </DashboardLayout>
              }
            />
          </Routes>
        </Router>
      </BorrowCartProvider>
    </>
  );
}

export default App;